<script lang="ts">
	import CoinMarketTableRow from './CoinMarketTableRow/CoinMarketTableRow.svelte';
	import type { FilterDataProperty, ITableData, sortDirectionType } from './types';
	import Pagination from 'components/Pagination/Pagination.svelte';
	import SortingArrow from 'components/SortingArrow/SortingArrow.svelte';
	import EmptyTable from 'components/EmptyTable/EmptyTable.svelte';
	import isInViewport from 'utils/isInViewport';
	import { onMount } from 'svelte';
	import { throttle, memoizeValue } from 'utils/decorators';
	import { _ } from 'utils/i18/i18n';
	import { getMarketsData } from 'services/coin-data/markets-data';
	import { browser } from '$app/environment';
	import activeCurrency from 'stores/activeCurrency';
	import IntersectionObserver from 'components/IntersectionObserver/IntersectionObserver.svelte';

	export let dark = false;
	export let route: string;
	export let isCustomCoin: boolean;
	export let symbol: string;
	export let coinPrice: number;
	export let name: string;

	let currentPage = 1;
	const isPageChanged = memoizeValue(currentPage);
	let isPressed = true;
	let coinMarketTableRef: HTMLElement;
	let isInUserViewport: boolean;
	let isLoaded: boolean;
	let totalPages: number;
	let tableData: ITableData[];
	let sortBy: FilterDataProperty = 'rank';
	let exchangesBaseCoinPrice = [];
	let isLoading = true;
	const perPage = 5;

	function handleFilterClick(e: Event & { currentTarget: HTMLElement }, order) {
		sortBy = e.currentTarget.id as FilterDataProperty;
		getMarketTable(order);
	}

	async function getMarketTable(order: sortDirectionType) {
		isLoading = true;
		if (!browser) return;
		try {
			const marketsData = await getMarketsData(
				route,
				symbol,
				currentPage,
				perPage,
				sortBy,
				order,
				$activeCurrency.currency.toUpperCase(),
			);
			tableData = marketsData?.data;
			totalPages = marketsData?.totalPages;
			exchangesBaseCoinPrice = marketsData.baseCoinMarketsData;
		} catch (error) {
			console.log('error', error);
		} finally {
			isLoading = false;
		}
	}

	onMount(() => {
		isInUserViewport = isInViewport(coinMarketTableRef);
		if (isInUserViewport) {
			void getMarketTable('asc');
		} else {
			document.addEventListener('scroll', handleScroll);
		}
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	});

	const handleScroll = throttle(() => {
		isInUserViewport = isInViewport(coinMarketTableRef);
	}, 300);

	$: symbol, (isLoaded = false);
	$: if (isInUserViewport && !isLoaded) {
		isLoaded = true;
		void getMarketTable('asc');
		document?.removeEventListener('scroll', handleScroll);
	}
	$: isPageChanged(currentPage) && void getMarketTable(isPressed ? 'asc' : 'desc');
</script>

<section>
	<h2 class="section-title container" class:dark>{name} markets</h2>
	<IntersectionObserver let:intersecting once={true} top={200}>
		{#if intersecting}
			<div class="wrapper" class:dark bind:this={coinMarketTableRef}>
				<table class="table">
					<thead class="table-head no-select">
						<tr class="table-row">
							<th class="head-cell number">#</th>
							<th
								id="pair"
								class="head-cell market clickable"
								on:click={(e) => {
									if (sortBy !== 'pair') isPressed = true;
									handleFilterClick(e, isPressed ? 'asc' : 'desc');
									isPressed = !isPressed;
								}}
							>
								{$_('coinDetailed.market')}
								<SortingArrow
									{isPressed}
									pressedIconId={sortBy}
									idToCompare={'pair'}
									width={11}
									height={7}
								/>
							</th>
							<th
								id="exchange"
								class="head-cell exchange clickable"
								on:click={(e) => {
									if (sortBy !== 'exchange') isPressed = true;
									isPressed = !isPressed;
									handleFilterClick(e, isPressed ? 'asc' : 'desc');
								}}
								>{$_('coinDetailed.exchange')}
								<SortingArrow
									{isPressed}
									pressedIconId={sortBy}
									idToCompare={'exchange'}
									width={11}
									height={7}
								/>
							</th>
							<th id="price" class="head-cell price clickable">{$_('general.price')} </th>
							<th class="head-cell change-price">24h price </th>
							<th
								id="volume"
								class="head-cell volume clickable"
								on:click={(e) => {
									if (sortBy !== 'volume') isPressed = true;
									isPressed = !isPressed;
									handleFilterClick(e, isPressed ? 'asc' : 'desc');
								}}
								>{$_('general.volume')}
								<SortingArrow
									{isPressed}
									pressedIconId={sortBy}
									idToCompare={'volume'}
									width={11}
									height={7}
								/>
							</th>
							<th class="head-cell change-volume">24h {$_('general.volume')} </th>
							<th class="head-cell liquidity">{$_('coinDetailed.liquidity')}, QTY</th>
							<th class="head-cell graph">{$_('general.volumeGraph')} </th>
						</tr>
					</thead>
					<tbody class="table-body">
						{#if !tableData || tableData.length === 0}
							<EmptyTable
								{dark}
								colAmountFromTabletView={9}
								colAmountMobView={9}
								tableType="coin-markets"
								withNoDataText={!isLoading}
								withLoadingText={isLoading}
							/>
						{:else}
							{#each tableData as item, i (item.marketId)}
								<CoinMarketTableRow
									data={item}
									number={i + (currentPage > 1 ? (currentPage - 1) * perPage : 0)}
									{dark}
									{isCustomCoin}
									{coinPrice}
									{symbol}
									{exchangesBaseCoinPrice}
								/>
							{/each}
						{/if}
					</tbody>
				</table>
			</div>
			<Pagination bind:currentPage noNavigation={true} {dark} totalCount={totalPages} />
		{/if}
	</IntersectionObserver>
</section>

<style lang="scss">
	@import 'coinMarketTable';
</style>
